import { useState } from 'react';
import { useProspectingAvailability } from '../tracker/useProspectingAvailability';
import { Badge, Button, InlineError } from '../../components/ui';
import { useJourneyHistory } from './journeyHistory';
import type { JourneyEvent, JourneySubject, JourneyVersion } from './journeyHistory';
import {
  eventProspect,
  historyEvents,
  historyEventTitle,
  historyEventDates,
  historyHeading,
  selectedVersion,
} from './journeyHistoryRows';
import './journeyHistory.css';

function EvidenceEvent({
  event,
  versions,
}: {
  event: JourneyEvent;
  versions: readonly JourneyVersion[];
}) {
  const selected = selectedVersion(event, versions);
  const previous = selectedVersion(event, versions, true);
  return (
    <li>
      <strong>{historyEventTitle(event)}</strong>
      <p className="tracker-field-note">
        {historyEventDates(event).join(' · ')} ·{' '}
        {event.actor_label ?? 'Recorded by system or unknown actor'}
      </p>
      {previous !== null && event.operation === 'message_corrected' && (
        <>
          <p>
            {previous.label}{' '}
            {previous.draft && <Badge>{previous.oneOff ? 'Draft · One-off' : 'Draft'}</Badge>}
            {!previous.reviewStateKnown && <Badge>Review status not recorded</Badge>}
          </p>
          <p className="journey-history-text">{previous.body}</p>
        </>
      )}
      {selected !== null ? (
        <>
          <p>
            {selected.label}{' '}
            {selected.draft && <Badge>{selected.oneOff ? 'Draft · One-off' : 'Draft'}</Badge>}
            {!selected.reviewStateKnown && <Badge>Review status not recorded</Badge>}
          </p>
          <p className="journey-history-text">{selected.body}</p>
          {selected.baseline && (
            <p className="tracker-field-note">
              Template observed at baseline; sent wording not verified.
            </p>
          )}
        </>
      ) : event.table_name === 'lead_activity' && event.facts.copy_set_id != null ? (
        <p className="tracker-field-note">Template reference; historical wording not recorded.</p>
      ) : null}
      <details>
        <summary>Recorded detail</summary>
        <pre className="journey-history-text">
          {JSON.stringify({ facts: event.facts, detail: event.detail }, null, 2)}
        </pre>
      </details>
    </li>
  );
}

/** Closed by default. Header counts come from the unpaged query, never from
 * rows already loaded. Removed records are read-only evidence, not live links. */
export function ProspectHistorySection({ subject }: { subject: JourneySubject }) {
  const { reader_enabled } = useProspectingAvailability();
  return reader_enabled ? <EnabledProspectHistorySection subject={subject} /> : null;
}

function EnabledProspectHistorySection({ subject }: { subject: JourneySubject }) {
  const history = useJourneyHistory(subject);
  const [open, setOpen] = useState(false);
  const pages = history.data?.pages ?? [];
  const first = pages[0];
  const denied = history.error?.message.includes('journey_not_permitted') === true;
  const state = history.isPending
    ? 'loading'
    : history.isError && first === undefined
      ? denied
        ? 'denied'
        : 'error'
      : undefined;
  const heading = historyHeading(first, state);
  const events = historyEvents(pages);
  const versions = pages.flatMap((page) => page.versions);
  const renderEvents = (rows: JourneyEvent[]) => (
    <ol className="journey-history-events">
      {rows.map((event) => (
        <EvidenceEvent key={event.id} event={event} versions={versions} />
      ))}
    </ol>
  );
  return (
    <section className="journey-history" aria-label="Prospect history">
      <Button
        variant="ghost"
        size="lg"
        className="journey-history-toggle"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {heading}
      </Button>
      {open && (
        <div>
          {denied ? (
            <p>You do not have access to this history.</p>
          ) : history.isError && first === undefined ? (
            <InlineError>History could not be loaded. Try again.</InlineError>
          ) : first === undefined ? (
            <p>Loading retained history...</p>
          ) : (
            <>
              <p className="tracker-field-note">
                Baseline at migration time · Earlier changes may be unavailable. A template
                selection is not proof of the exact sent text.
              </p>
              {first.prospects.length === 0 && (
                <p>
                  No prospect origin is recorded for this{' '}
                  {subject.table === 'deals'
                    ? 'deal'
                    : subject.table === 'leads'
                      ? 'lead'
                      : 'record'}
                  .
                </p>
              )}
              {first.attribution_needs_review > 0 && (
                <InlineError>
                  Attribution needs review: {first.attribution_needs_review} DM entries have
                  conflicting origins.
                </InlineError>
              )}
              {first.prospects.map((prospect) => (
                <section key={prospect.id}>
                  <h3>
                    {prospect.label} · {prospect.dm_count} {prospect.dm_count === 1 ? 'DM' : 'DMs'}{' '}
                    as prospect
                  </h3>
                  {prospect.dm_count === 0 && <p>No prospect DMs recorded.</p>}
                  {renderEvents(
                    events.filter((event) => eventProspect(event, first) === prospect.id),
                  )}
                </section>
              ))}
              <section>
                <h3>Related record history</h3>
                {renderEvents(events.filter((event) => eventProspect(event, first) === null))}
              </section>
              {history.isFetchNextPageError && (
                <InlineError>
                  More history could not be loaded. Entries already shown may be incomplete.
                </InlineError>
              )}
              {history.hasNextPage && (
                <Button
                  variant="secondary"
                  disabled={history.isFetchingNextPage}
                  onClick={() => void history.fetchNextPage()}
                >
                  {history.isFetchingNextPage ? 'Loading...' : 'Show older retained history'}
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
